/**
 * Header message board (Gordon 2026-10-30): the header's background becomes a
 * full-height ticker of messages, GIFs and film clips with links — BB news set
 * in Super Admin, unread chat messages, and new releases / events / products
 * (from the artists and people a user follows, when they follow any).
 *
 *   settings/headerBoard — Super Admin config (public read, admin write)
 */
import { db } from '@/lib/firebase';
import { deleteField, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { getBoardReleases, getUpcomingEvents } from '@/services/home';
import { getMarketplaceItems } from '@/services/marketplace';
import { getFollowing } from '@/services/follows';
import { getNewsArticles } from '@/services/home';
import { catalogueDate } from '@/lib/catalogue-analytics';
import { newsOrigin } from '@/lib/home-news';
import { newsHref } from '@/lib/news-image';
import { DEFAULT_NEWS_TOPICS, type BoardItemKind, type BoardStyle, type NewsTopics } from '@/services/user-layout-prefs';

export type { BoardItemKind };

export interface BoardMessage {
  id: string;
  text: string;
  href?: string;
  /** Image or GIF shown beside the text */
  imageUrl?: string;
  /** Short muted, looping film clip (mp4/webm) — wins over imageUrl */
  videoUrl?: string;
}

/** Real lines for the visitor board (GET /api/header-board/visitor): the
 *  newest messages in the PUBLIC rooms Super Admin picked, and open collab
 *  posts. Never sample/dummy text (Gordon 2026-11-01). */
export interface VisitorBoardFeed {
  chats: { id: string; roomId: string; roomName: string; senderName: string; text: string; at: number }[];
  collabs: { id: string; title: string; ownerName: string; roles: string[] }[];
}

export async function loadVisitorBoard(): Promise<VisitorBoardFeed> {
  const locale = typeof document !== 'undefined' ? document.documentElement.lang || 'en' : 'en';
  const res = await fetch(`/api/header-board/visitor?locale=${encodeURIComponent(locale)}`);
  if (!res.ok) return { chats: [], collabs: [] };
  const j = await res.json();
  return { chats: Array.isArray(j.chats) ? j.chats : [], collabs: Array.isArray(j.collabs) ? j.collabs : [] };
}

export interface HeaderBoardSettings {
  enabled: boolean;
  /** Glide speed, pixels per second */
  speed: number;
  /** Quiet seconds after a message has passed before the next (default 9, min 6) */
  gapSec: number;
  messages: BoardMessage[];
  /** Public group rooms whose newest messages visitors see (real chat, not samples) */
  visitorRoomIds: string[];
  /** What visitors see besides the messages */
  visitor: { chat: boolean; releases: boolean; news: boolean; events: boolean; products: boolean };
  /** Visitors' font + colour per kind (users set their own) */
  styles: Partial<Record<BoardItemKind, BoardStyle>>;
  /** Visitors' news topics (users set their own) */
  newsTopics: NewsTopics;
}

export const DEFAULT_HEADER_BOARD: HeaderBoardSettings = {
  enabled: true,
  speed: 90,
  // Quiet seconds between messages (Gordon 2026-10-30: "further apart, 18 seconds").
  gapSec: 18,
  messages: [],
  visitorRoomIds: [],
  visitor: { chat: true, releases: true, news: true, events: true, products: true },
  styles: {},
  newsTopics: DEFAULT_NEWS_TOPICS,
};

export interface BoardItem {
  id: string;
  kind: BoardItemKind;
  text: string;
  sub?: string;
  href?: string;
  imageUrl?: string;
  videoUrl?: string;
  /** Window event to dispatch on click instead of navigating (chat → openChatRoom) */
  event?: { name: string; detail: Record<string, unknown> };
  /** Who it is from — a person-specific style wins over the kind's */
  personId?: string;
}

const settingsRef = () => doc(db, 'settings', 'headerBoard');

export function subscribeHeaderBoardSettings(cb: (s: HeaderBoardSettings) => void): () => void {
  return onSnapshot(settingsRef(), snap => {
    const d = (snap.exists() ? snap.data() : {}) as Partial<HeaderBoardSettings>;
    cb({
      ...DEFAULT_HEADER_BOARD,
      ...d,
      messages: Array.isArray(d.messages) ? d.messages.filter(m => m && m.text) : [],
      visitorRoomIds: Array.isArray(d.visitorRoomIds) ? d.visitorRoomIds.filter(id => typeof id === 'string' && id) : [],
      visitor: { ...DEFAULT_HEADER_BOARD.visitor, ...(d.visitor || {}) },
      styles: d.styles || {},
      newsTopics: { ...DEFAULT_NEWS_TOPICS, ...(d.newsTopics || {}) },
    });
  }, () => cb(DEFAULT_HEADER_BOARD));
}

export async function saveHeaderBoardSettings(s: HeaderBoardSettings): Promise<void> {
  // Firestore rejects undefined — drop empty optional fields.
  const messages = s.messages.map(m => Object.fromEntries(Object.entries(m).filter(([, v]) => v !== undefined && v !== '')));
  // The retired sample chat lines are removed, never shown again.
  await setDoc(settingsRef(), { ...s, messages, demoChats: deleteField() }, { merge: true });
}

/** Releases, events and products for the board. With a uid, releases narrow to
 *  the artists / people that user follows (latest ones when nothing matches). */
export interface BoardFeed { releases: BoardItem[]; news: BoardItem[]; events: BoardItem[]; products: BoardItem[] }

export async function loadBoardFeed(opts: { uid?: string; releases: boolean; news: boolean; newsTopics?: NewsTopics; events: boolean; products: boolean }): Promise<BoardFeed> {
  const [releases, events, products, following, articles] = await Promise.all([
    opts.releases ? getBoardReleases().catch(() => []) : Promise.resolve([]),
    opts.events ? getUpcomingEvents().catch(() => []) : Promise.resolve([]),
    opts.products ? getMarketplaceItems().catch(() => []) : Promise.resolve([]),
    opts.uid && opts.releases ? getFollowing(opts.uid).catch(() => []) : Promise.resolve([]),
    opts.news ? getNewsArticles().catch(() => []) : Promise.resolve([]),
  ]);

  const followed = new Set((following as any[]).map(f => f.targetId).filter(Boolean));
  const mine = followed.size ? releases.filter(r => followed.has(r.artistId) || followed.has(r.ownerUid)) : [];
  const releaseItems: BoardItem[] = (mine.length ? mine : releases).slice(0, 8).map(r => ({
    id: `release-${r.id}`, kind: 'release', text: r.title, sub: r.artist, href: `/music/${r.id}`, imageUrl: r.imageUrl || undefined,
    personId: r.ownerUid || r.artistId || undefined,
  }));
  // Only the news kinds the viewer wants: music tech, music business, BB's
  // own. The scraper only takes tech + business feeds, so general news about
  // artists outside BB never reaches the board.
  const topics = opts.newsTopics ?? DEFAULT_NEWS_TOPICS;
  const stamp = (a: any) => catalogueDate(a.publishedAt)?.getTime() || catalogueDate(a.createdAt)?.getTime() || 0;
  const wanted = (articles as any[]).filter(a => a.status !== 'draft' && topics[newsOrigin(a)]).sort((x, y) => stamp(y) - stamp(x));
  const newsItems: BoardItem[] = wanted.slice(0, 8).map(a => ({
    // newsHref: legacy Squarespace slugs contain slashes and 404 as /news/<slug>.
    id: `news-${a.id}`, kind: 'news', text: a.title, href: newsHref(a), imageUrl: a.imageUrl || undefined,
  }));
  const eventItems: BoardItem[] = events.slice(0, 6).map(e => ({
    id: `event-${e.id}`, kind: 'event', text: e.title, sub: [e.date, e.location].filter(v => v && v !== 'TBA').join(' · '), href: `/events/${e.id}`, imageUrl: e.imageUrl || undefined,
  }));
  const productItems: BoardItem[] = (products as any[])
    .filter(p => !p.status || p.status === 'active')
    .slice(0, 6)
    .map(p => {
      const media = Array.isArray(p.media) ? [...p.media].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : [];
      const image = media.find((m: any) => m.type === 'image')?.url || p.imageUrl || '';
      return { id: `product-${p.itemId || p.id}`, kind: 'product' as const, text: p.title || 'Product', href: `/marketplace/${p.itemId || p.id}`, imageUrl: image || undefined };
    });

  return { releases: releaseItems, news: newsItems, events: eventItems, products: productItems };
}
